import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MyTest, myTestApi, showTime } from '../../utils/myTestApi';
import { getSchool } from '../../utils/schoolSession';

/**
 * The tests a school student's teachers have set: open now, coming up, done.
 *
 * Whether a test can be started is the API's answer (`can_start`, and
 * `cannot_start_reason` when not), worked out on the server's clock. This
 * page never compares times itself; it asks again every minute, so a test
 * that opens while the page is up becomes startable without a reload.
 */

const STATE_BADGE: Record<string, [string, string]> = {
  assigned: ['Not started', 'bg-secondary'],
  in_progress: ['In progress', 'bg-info text-dark'],
  submitted: ['Submitted', 'bg-success'],
  auto_submitted: ['Submitted (time ran out)', 'bg-success'],
  missed: ['Missed', 'bg-warning text-dark'],
  excused: ['Excused', 'bg-light text-dark border'],
};

const TestCard: React.FC<{ t: MyTest; onOpen: (t: MyTest) => void }> = ({ t, onOpen }) => {
  const [label, cls] = STATE_BADGE[t.state] || [t.state, 'bg-secondary'];
  const done = t.state === 'submitted' || t.state === 'auto_submitted';
  const action = t.in_progress ? 'Continue' : t.can_start ? (t.attempts_used > 0 ? 'Try again' : 'Start') : done ? 'View' : null;

  return (
    <div className="card shadow-sm h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
          <h5 className="card-title mb-0">{t.title}</h5>
          <span className={`badge ${cls}`}>{label}</span>
        </div>
        <div className="small text-muted mb-2">
          <div>
            <i className="far fa-clock me-1" />
            {t.duration_minutes} minutes
            {t.max_attempts > 1 && ` · ${t.attempts_used} of ${t.max_attempts} attempts used`}
          </div>
          <div>
            <i className="far fa-calendar me-1" />
            {showTime(t.scheduled_start)} – {showTime(t.scheduled_end)}
          </div>
        </div>

        {t.in_progress && t.seconds_left !== null && (
          <div className="alert alert-info py-1 px-2 small mb-2">About {Math.ceil(t.seconds_left / 60)} minutes left.</div>
        )}
        {done && t.results_visible && t.score !== null && (
          <div className="fs-5 mb-2">
            Score <strong>{t.score}</strong> <span className="text-muted small">({t.percentage}%)</span>
          </div>
        )}
        {done && !t.results_visible && (
          <div className="small text-muted mb-2">
            {t.show_results_mode === 'after_window' && t.results_available_at
              ? `Results on ${showTime(t.results_available_at)}.`
              : t.show_results_mode === 'manual'
              ? 'Your teacher will release the results.'
              : 'Your teacher has not shared results for this test.'}
          </div>
        )}
        {!t.can_start && !t.in_progress && !done && t.cannot_start_reason && (
          <div className="small text-muted mb-2">{t.cannot_start_reason}</div>
        )}

        <div className="mt-auto">
          {action && (
            <button className={`btn ${action === 'View' ? 'btn-outline-primary' : 'btn-primary'} w-100`} onClick={() => onOpen(t)}>
              {action}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; tests: MyTest[]; empty: string; onOpen: (t: MyTest) => void }> = ({ title, tests, empty, onOpen }) => (
  <section className="mb-5">
    <h5 className="mb-3">{title}</h5>
    {tests.length === 0 ? (
      <p className="text-muted">{empty}</p>
    ) : (
      <div className="row g-3">
        {tests.map(t => (
          <div key={t.assignment_id} className="col-12 col-md-6 col-lg-4">
            <TestCard t={t} onOpen={onOpen} />
          </div>
        ))}
      </div>
    )}
  </section>
);

const MyTests: React.FC = () => {
  const navigate = useNavigate();
  const school = getSchool();
  const [tests, setTests] = useState<MyTest[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    myTestApi
      .list()
      .then(r => {
        setTests(r.result);
        setError(null);
      })
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const open = (t: MyTest) => {
    if (t.in_progress || t.can_start) {
      navigate(`/my-tests/${t.assignment_id}/take`);
    } else {
      navigate(`/my-tests/${t.assignment_id}`);
    }
  };

  if (error && tests === null) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }
  if (tests === null) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const now = tests.filter(t => t.in_progress || (t.window_state === 'open' && !['submitted', 'auto_submitted', 'missed', 'excused'].includes(t.state)) || t.can_start);
  const nowIds = new Set(now.map(t => t.assignment_id));
  const upcoming = tests.filter(t => !nowIds.has(t.assignment_id) && t.window_state === 'upcoming');
  const done = tests.filter(t => !nowIds.has(t.assignment_id) && t.window_state !== 'upcoming');

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h3 className="mb-0">My Tests</h3>
        {school && <div className="text-muted">{school.name}</div>}
      </div>
      <Section title="Open now" tests={now} empty="Nothing to take right now." onOpen={open} />
      <Section title="Coming up" tests={[...upcoming].reverse()} empty="No tests scheduled." onOpen={open} />
      <Section title="Done and closed" tests={done} empty="Nothing here yet." onOpen={open} />
    </div>
  );
};

export default MyTests;
